import React, { useEffect, useRef, useState } from "react";
import { DataGrid, GridColDef, GridRowId } from "@mui/x-data-grid";
import { InventoryApiService } from "../../Service/InventoryApiService";
import { Service } from "../../Service/ApiService";
import DataGridLoader from '../Common/DataGridLoader';
import { Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import CheckIcon from '@mui/icons-material/Check';
import { CommonService } from "../../Service/CommonService";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MultiSelectChipInput from "../Common/MultiSelectChipInput";
import { Accordion } from "react-bootstrap";

const CompanyInventorySummaryReport: React.FC = () => {
  const [companiesList, setCompaniesList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState("companyName"); // Default sort
  const [sortDirection, setSortDirection] = useState("asc"); // Default ascending
  const controllerRef = useRef<AbortController | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const containerTypeOptions = ["BARREL", "DRUM", "TOTE",];
  const [filter, setFilter] = useState<any>({
    companyName: null,
    containerType: null,
    containerCount: null
  });

  // MultiSelectChipInput state for company filter
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [allCompanyOptions, setAllCompanyOptions] = useState<string[]>([]);

  const isFilterApplied = Object.values(filter).some(
    (value) => value !== null && value !== "" && value !== undefined
  );
  const localeText = {
    noRowsLabel: isLoading ? "Fetching records ..." : "No Record Found",
  };

  useEffect(() => {
    getCompaniesContainerCount(page, pageSize, filter, sortField, sortDirection);
    // Fetch company options for the multiselect
    fetchCompanyOptions();
  }, []);

  const fetchCompanyOptions = async () => {
    try {
      const response: any = await Service.fetchCompaniesList();
      const names: string[] = (response.data || [])
        .map((c: any) => c.companyName)
        .filter(Boolean)
        .sort();
      setAllCompanyOptions(names);
    } catch (_) { /* silent */ }
  };


  //add tooltip
  const CellWithTooltip = ({ value }: { value: string | number }) => {
    return (
      <Tooltip arrow title={value?.toString()}>
        <span>{value?.toString()}</span>
      </Tooltip>
    );
  };

  const getCompaniesContainerCount = async (page, size, filter: any, sortField: any, sortDirection: any) => {

    const controller = new AbortController();
    const signal = controller.signal;
    try {
      setIsLoading(true);
      setCompaniesList([]);
      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;

      const response: any = await InventoryApiService.getCompaniesContainerCount(page, size, filter, sortField, sortDirection, signal);
      setCompaniesList(response.data.content);
      setTotalCount(response.data.totalElements);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  }

  const exportCompanyInventorySummary = async (filter: any, sortField: any, sortDirection: any) => {
    const controller = new AbortController();
    const signal = controller.signal;

    try {
      setIsLoading(true);

      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;
      InventoryApiService.exportCompanyInventorySummary(filter, sortField, sortDirection, signal);
      CommonService.Toast.fire({
        title: "Your export is in progress. We'll notify you when it's ready!",
        icon: "success",
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  };

  const tableDataWithIds = companiesList.map((rowData, index) => ({
    ...rowData,
  }));

  const handleSortChange = (sortModel) => {
    if (sortModel.length > 0) {
      const newSortField = sortModel[0].field;
      const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid

      setSortField(newSortField);
      setSortDirection(newSortDirection);

      getCompaniesContainerCount(page, pageSize, filter, newSortField, newSortDirection);
    }
  };

  const onPaginationModelChange = (newPaginationModel) => {
    setPage(newPaginationModel.page);
    setPageSize(newPaginationModel.pageSize);

    getCompaniesContainerCount(newPaginationModel.page, newPaginationModel.pageSize, filter, sortField, sortDirection);
  };

  const handleFilterReset = (field: keyof any) => {
    const tempFilter = {
      ...filter,
      [field]: null
    };
    if (field === 'companyName') setSelectedCompanies([]);
    setFilter(tempFilter);
  };

  const handleFilterChange = (event: any, field: keyof any) => {
    let value = event.target.value;

    const tempFilter = {
      ...filter,
      [field]: value
    };

    // Update the filter state
    setFilter(tempFilter);
  };

  const handleClear = () => {
    const tempFilter = {
      companyName: null,
      containerType: null,
      containerCount: null
    };

    setSelectedCompanies([]);
    setFilter(tempFilter);
    getCompaniesContainerCount(page, pageSize, tempFilter, sortField, sortDirection);
  }

  const handleApply = () => {
    // Sync selectedCompanies into filter before applying
    const tempFilter = {
      ...filter,
      companyName: selectedCompanies.length > 0 ? selectedCompanies.join(",") : null,
    };
    setFilter(tempFilter);
    getCompaniesContainerCount(page, pageSize, tempFilter, sortField, sortDirection);
  };

  const handleCheckboxChange = (e: any, key: string) => {
    const { value, checked } = e.target;

    let selectedValues = filter[key] ? filter[key].split(",") : [];

    if (checked) {
      selectedValues.push(value);
    } else {
      selectedValues = selectedValues.filter((item: string) => item !== value);
    }

    setFilter((prev: any) => ({
      ...prev,
      [key]: selectedValues.join(","),
    }));
  };

  const columns: GridColDef[] = [
    {
      field: "companyName",
      headerName: "Company Name",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.companyName ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
            {params.colDef.headerName}
          </Typography>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.row.companyName} />
      ),
    },
    {
      field: "containerType",
      headerName: "Container Type",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.containerType ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
            {params.colDef.headerName}
          </Typography>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "containerCount",
      headerName: "Container Count",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => `${row.containerCount ?? ""}`,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
            {params.colDef.headerName}
          </Typography>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    }
  ];

  return (<>
    <div className="reporting-filter-btn-wrapper company-summary-rpt">

        {companiesList.length > 0 || isLoading ?
          <>
            <button title="Export to Excel"
              className="primary-btn d-inline-flex align-items-center py-1 export-btn"
              onClick={(e) => {
                e.currentTarget.blur(); // Removes focus after clicking
                exportCompanyInventorySummary(filter, sortField, sortDirection);
              }}
            >
              <FileDownloadOutlinedIcon sx={{ marginTop: "5.5px" }} />
              Export
            </button>
          </>
        : null}
    </div>
    <div className="user-table-container report-module">
      <Accordion defaultActiveKey="0" className="filter-accordion">
        <Accordion.Item eventKey="0" className="filter-accordion-item">
          <Accordion.Header className="filter-accordion-header">
            <span className="accordion-filter-title">Filters</span>
            <button
              className="filter-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            >
              Reset All
            </button>
          </Accordion.Header>
          <Accordion.Body className="filter-accordion-body p-0">
            <div className="filter-panel-accordion">

              <div className="filter-body">

                {/* Company — MultiSelectChipInput */}
                <div className="filter-section">
                  <div className="section-header">
                    <h4>Company</h4>
                    <button className="section-btn" type="button" onClick={() => handleFilterReset("companyName")}>Reset</button>
                  </div>
                  <MultiSelectChipInput
                    selected={selectedCompanies}
                    options={allCompanyOptions}
                    placeholder="Select companies"
                    searchPlaceholder="Search company"
                    onToggle={val => setSelectedCompanies(prev =>
                      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
                    )}
                    onSelectAll={() => setSelectedCompanies(prev =>
                      prev.length === allCompanyOptions.length ? [] : [...allCompanyOptions]
                    )}
                    onReset={() => setSelectedCompanies([])}
                  />
                </div>

                {/* Container Type */}
                <div className="filter-section">
                  <div className="section-header">
                    <h4>Container Type</h4>
                    <button className="section-btn" type="button" onClick={() => handleFilterReset("containerType")}>Reset</button>
                  </div>
                  <div className="checkbox-group">
                    {containerTypeOptions.map((type) => {
                      const selectedValues = filter.containerType
                        ? filter.containerType.split(",")
                        : [];

                      return (
                        <div key={type} className="form-check">
                          <input
                            type="checkbox"
                            id={type}
                            value={type}
                            checked={selectedValues.includes(type)}
                            onChange={(e) => handleCheckboxChange(e, "containerType")}
                            className="form-check-input"
                          />
                          <label htmlFor={type} className="form-check-label m-0">
                            {type}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Container Count */}
                <div className="filter-section">
                  <div className="section-header">
                    <h4>Container Count</h4>
                    <button className="section-btn" type="button" onClick={() => handleFilterReset("containerCount")}>Reset</button>
                  </div>
                  <input
                    type="number"
                    min="0"
                    placeholder="Enter container count"
                    className="form-control"
                    value={filter.containerCount ?? ""}
                    onKeyDown={(e) =>
                      (e.key === '-' || (e.key === 'ArrowDown' && Number(filter.containerCount) <= 0)) &&
                      e.preventDefault()
                    }
                    onPaste={(e) => {
                      const paste = e.clipboardData.getData('text');
                      if (paste.includes('-')) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(event) => handleFilterChange(event, "containerCount")}
                  />
                </div>

              </div>
              <div className="filter-bottom">
                <div className="filter-actions">
                  <button className="filter-btn" onClick={handleApply}><CheckIcon /> Apply</button>
                </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <div className="userTable">
        <DataGrid
          rows={tableDataWithIds}
          columns={columns}
          className="data-grid"
          pagination
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={onPaginationModelChange}
          rowCount={totalCount}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={handleSortChange}
          disableRowSelectionOnClick={true}
          pageSizeOptions={[10, 20, 50, 100]}
          localeText={localeText}
          getRowId={(row) => `${row.companyName}-${row.containerType}`}
          loading={isLoading}
          slots={{
            loadingOverlay: DataGridLoader,
          }}
          disableColumnResize
          sx={{
            "& .MuiDataGrid-columnHeaderCheckbox": {
              borderBottom: "unset !important"
            },
          }}
        />
      </div>
    </div>

  </>
  );
}

export default CompanyInventorySummaryReport;
